import React, { useState, useEffect } from 'react';
import Toggle from 'react-toggle';
import { NewsletterOptionProps } from '../types/Newsletter';
import Icon from '../../common/components/Icon';
import Chip from '../../components/chip/Chip';
import { useTranslations } from '../../localization/useTranslations';
import PremiumNewsletterModal from '../../newsletter-landing/components/PremiumNewsletterModal';
import { getNewsletterDetail } from '../../utils/getNewsletterFields';
import { handleNewsletterToggle } from '../../newsletter-landing/helpers/handleNewsletterToggle';
import { checkIfAlreadySubscribedToPremium } from '../../newsletter-landing/helpers/CheckIfAlreadySubscribedToPremium';

const NewsletterOption: React.FC<NewsletterOptionProps> = ({
  newsletter,
  isSubscribed,
  onChange,
  shouldOpenPremodal,
  isUserPremium,
  isLoading,
  setIsLoading,
}) => {
  const translations = useTranslations();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isToggleChecked, setIsToggleChecked] = useState(isSubscribed);
  const [isAlreadySubscribedToPremium, setIsAlreadySubscribedToPremium] = useState<boolean>(false);

  const newsletterPeriod = getNewsletterDetail(newsletter, 'period');
  const newsletterTitle = getNewsletterDetail(newsletter, 'title');

  const modalContent = {
    ...window.premiumModalFields,
    header_background_image:
      typeof window.premiumModalFields?.header_background_image === 'string'
        ? window.premiumModalFields?.header_background_image
        : undefined,
  };

  // Update the toggle state when the subscription status changes
  useEffect(() => {
    setIsToggleChecked(isSubscribed);
  }, [isSubscribed]);

  // Use the helper function to check if the user is already subscribed to the premium newsletter
  useEffect(() => {
    const isSubscribedToPremium = checkIfAlreadySubscribedToPremium(newsletter.code);

    setIsAlreadySubscribedToPremium(isSubscribedToPremium);
  }, [newsletter.code]);

  const handleToggleClick = () => {
    if (isLoading) return; // Prevent toggle if loading
    setIsLoading(true); // Set loading state
    handleNewsletterToggle(
      newsletter,
      isUserPremium ?? false,
      isAlreadySubscribedToPremium ?? false,
      setIsDialogOpen,
      setIsToggleChecked,
      onChange,
      setIsAlreadySubscribedToPremium,
      setIsLoading
    );
  };

  useEffect(() => {
    setIsDialogOpen(shouldOpenPremodal ?? false);
  }, [shouldOpenPremodal]);

  return (
    <div className="newsletter-option newsletter-option--react">
      <div className="newsletter-option__content">
        <div className="newsletter-option__content-wrapper">
          <div className="newsletter-option__wrapper">
            <div className="newsletter-option__header">
              <div className="newsletter-option__link-wrapper" onClick={handleToggleClick}>
                <Toggle
                  checked={isToggleChecked}
                  onChange={handleToggleClick}
                  disabled={isLoading} // Disable toggle when loading
                  aria-label={newsletterTitle || 'Newsletter toggle'}
                />
                <h5>{newsletterTitle}</h5>
              </div>

              {newsletter.is_premium && <Chip name="Premium" color="amber" type="outline" iconName="diamond-empty" />}
            </div>

            <div className="newsletter-option__body">
              {newsletterPeriod && <span className="newsletter-option__period tag-text">{newsletterPeriod}</span>}

              <p className="small-description">{newsletter.description}</p>

              {newsletter.preview_url && (
                <a
                  href={newsletter.preview_url}
                  className="link link-14 newsletter-option__preview"
                  target="_blank"
                  rel="noreferrer"
                >
                  <span className="link__text">{translations?.newsletter?.previewLabel || 'View preview'}</span>
                  <Icon name="external-link" size="small" />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Premium Newsletter Modal */}
      {newsletter.is_premium && (
        <PremiumNewsletterModal
          isOpen={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          modalContent={modalContent}
        />
      )}
    </div>
  );
};

export default NewsletterOption;
